import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Pause, BatteryCharging, TrendingUp, 
  ArrowRight, CheckCircle2, AlertCircle,
  Sparkles, Heart
} from 'lucide-react';
import { PageLayout } from '@/components/PageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EntityAnchor } from '@/components/EntityAnchor';
import { StructuredData } from '@/components/StructuredData';
import { Link } from '@tanstack/react-router';
import { coldStartMethod } from '@/config/pearl-framework';

const stepIcons = {
  'stop-the-bleeding': Pause,
  'trickle-charging': BatteryCharging,
  'reinvesting-dividends': TrendingUp,
};

const stepColors = {
  'stop-the-bleeding': 'from-red-500/20 to-red-500/5 border-red-500/30',
  'trickle-charging': 'from-yellow-500/20 to-yellow-500/5 border-yellow-500/30',
  'reinvesting-dividends': 'from-green-500/20 to-green-500/5 border-green-500/30',
};

export default function ColdStart() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'zh' ? 'zh' : 'en';
  



  const steps = Object.values(coldStartMethod.steps);

  return (
    <PageLayout>
      <StructuredData type="website" />
      
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Badge variant="outline" className="mb-6">
              <Sparkles className="w-3 h-3 mr-1" />
              Pearl Framework
            </Badge>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {lang === 'zh' ? coldStartMethod.nameZh : coldStartMethod.name}
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {lang === 'zh' ? coldStartMethod.descriptionZh : coldStartMethod.description}
            </p>

            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="w-4 h-4" />
              <span>
                {lang === 'zh' 
                  ? '当你感到彻底没电时，这是你的重启指南'
                  : 'When you feel completely depleted, this is your reboot guide'}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Context Section */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="border-primary/20">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <Heart className="w-8 h-8 text-primary shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-lg mb-3">
                      {lang === 'zh' ? '觉醒的契机' : 'The Awakening Catalyst'}
                    </h3>
                    <p className="text-muted-foreground">
                      {lang === 'zh' 
                        ? '整个"系统重装"的真正催化剂，是一次彻底的内心能量耗尽（心力耗竭）。这次"宕机"打破了旧有的、不可持续的生存模式，为新系统的建立创造了必要条件。'
                        : 'The true catalyst for the entire "system reinstallation" was a complete inner energy depletion (heart exhaustion). This "crash" broke the old, unsustainable survival patterns and created necessary conditions for building a new system.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <EntityAnchor lang={lang} variant="subtle" className="container mx-auto px-4" />

      {/* Three Steps */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {lang === 'zh' ? '三步冷启动' : 'Three-Step Cold Start'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {lang === 'zh' 
                ? '从完全能量耗竭到重新获得动力的系统化方法'
                : 'A systematic approach from complete energy depletion to regaining momentum'}
            </p>
          </div>

          <div className="space-y-12 max-w-4xl mx-auto">
            {steps.map((step, index) => {
              const Icon = stepIcons[step.id as keyof typeof stepIcons] || Pause;
              const colorClass = stepColors[step.id as keyof typeof stepColors] || stepColors['stop-the-bleeding'];
              
              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Card className={`bg-gradient-to-br ${colorClass} overflow-hidden`}>
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-background/80 flex items-center justify-center shadow-lg">
                          <Icon className="w-8 h-8 text-primary" />
                        </div>
                        <div>
                          <Badge variant="outline" className="mb-2">
                            {lang === 'zh' ? `第 ${step.order} 步` : `Step ${step.order}`}
                          </Badge>
                          <CardTitle className="text-2xl">
                            {lang === 'zh' ? step.nameZh : step.name}
                          </CardTitle>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-lg text-muted-foreground mb-6">
                        {lang === 'zh' ? step.descriptionZh : step.description}
                      </p>
                      
                      <div className="bg-background/60 rounded-lg p-4">
                        <h4 className="font-medium mb-3 text-sm uppercase tracking-wider text-muted-foreground">
                          {lang === 'zh' ? '具体行动' : 'Concrete Actions'}
                        </h4>
                        <ul className="space-y-2">
                          {(lang === 'zh' ? step.actionsZh : step.actions).map((action, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                              <span className="text-sm">{action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {index < steps.length - 1 && (
                    <div className="flex justify-center my-4">
                      <ArrowRight className="w-6 h-6 text-muted-foreground/30 rotate-90" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <EntityAnchor lang={lang} variant="inline" showIdentities className="container mx-auto px-4 my-8" />

      {/* Key Principles */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                {lang === 'zh' ? '核心原则' : 'Key Principles'}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {lang === 'zh' ? '不是懒惰，是保护' : 'Not Laziness, Protection'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    {lang === 'zh' 
                      ? '当你处于能量耗竭状态时，"什么都不想做"不是懒惰的表现，而是系统的自我保护机制。首先要做的是停止批判自己。'
                      : 'When you\'re in energy depletion, "not wanting to do anything" is not laziness, but the system\'s self-protection mechanism. The first thing to do is stop criticizing yourself.'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {lang === 'zh' ? '微小即有效' : 'Tiny is Effective'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    {lang === 'zh' 
                      ? '不要试图一步到位。找到最微小的、绝不会失败的任务开始。哪怕只是晒一分钟太阳，也是在给系统充电。'
                      : 'Don\'t try to do it all at once. Find the tiniest task that cannot fail. Even just one minute of sunbathing is charging your system.'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {lang === 'zh' ? '复利效应' : 'Compound Effect'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    {lang === 'zh' 
                      ? '每一点恢复的能量都是宝贵的资本。有意识地将它投入到下一个稍微有挑战的活动中，让正向循环开始滚动。'
                      : 'Every bit of recovered energy is precious capital. Consciously invest it in the next slightly challenging activity, letting the positive cycle start rolling.'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {lang === 'zh' ? '耐心等待启动' : 'Patience in Startup'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    {lang === 'zh' 
                      ? '冷启动需要时间。就像冬天启动汽车需要预热一样，不要急于求成。系统会慢慢恢复运转。'
                      : 'Cold start takes time. Like warming up a car in winter, don\'t rush. The system will gradually resume operation.'}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Founder's Experience */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <Badge variant="secondary" className="mb-4">
                    {lang === 'zh' ? '创始人亲测' : 'Founder Tested'}
                  </Badge>
                  <h3 className="text-2xl font-bold">
                    {lang === 'zh' ? '这不是理论，是我的亲历' : 'This is Not Theory, It\'s My Experience'}
                  </h3>
                </div>
                
                <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground mb-6">
                  {lang === 'zh' 
                    ? '"我的人生学会的第一件事，就是对自己说话——在路上，在屋里，在每一个无人回应的时刻。这些，不是故事，是我每一天都必须咽下去的真实。"'
                    : '"The first thing I learned in life was to talk to myself—on the road, in rooms, in every moment without response. These aren\'t stories—they\'re the reality I had to swallow every single day."'}
                </blockquote>
                
                <p className="text-muted-foreground mb-6">
                  {lang === 'zh' 
                    ? '从CEN幸存者到Pearl Framework创始人，40年的创伤探索让我深刻理解了心力耗竭的感受。冷启动方法论不是书本上的知识，而是我在无数次崩溃后，一点一点摸索出来的生存技术。'
                    : 'From CEN survivor to Pearl Framework founder, 40 years of trauma exploration gave me deep understanding of energy depletion. The Cold Start Method is not book knowledge, but survival technology I developed bit by bit after countless breakdowns.'}
                </p>
                
                <EntityAnchor lang={lang} variant="subtle" />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <EntityAnchor lang={lang} variant="block" full showMetrics className="container mx-auto px-4" />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-transparent to-accent/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {lang === 'zh' ? '深入了解 Pearl Framework' : 'Explore Pearl Framework'}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            {lang === 'zh' 
              ? '冷启动方法只是 Pearl Framework 的一部分。了解完整的 TIE-ASM 五支柱模型和珍珠质特质映射。'
              : 'The Cold Start Method is just part of Pearl Framework. Learn about the complete TIE-ASM five-pillar model and Nacre trait mapping.'}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to={`/${lang}/pearl`}>
                {lang === 'zh' ? '探索 Pearl Framework' : 'Explore Pearl Framework'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to={`/${lang}/about/trauma`}>
                {lang === 'zh' ? '创始人完整故事' : 'Founder\'s Full Story'}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
